import { yupResolver } from '@hookform/resolvers/yup';
import { memo, useCallback, useEffect, useId, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { type InferType, object, string } from 'yup';

import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { Label } from '@air/primitive-label';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useCanvas } from '~/swr-hooks/canvases/useCanvas';
import { usePrivateCanvasesUpdateCanvasMutation } from '~/swr-hooks/canvases/useUpdateCanvas';

export const EditCanvasDetailsModalValidationSchema = object({
  canvasName: string().required('Canvas name is required'),
});

export type EditCanvasDetailsModalInput = InferType<typeof EditCanvasDetailsModalValidationSchema>;

export type EditCanvasDetailsModalProps = {
  canvasId: string;
} & AirModalProps;

export const EditCanvasDetailsModal = memo(({ canvasId, onClose }: EditCanvasDetailsModalProps) => {
  const formId = useId();
  const { currentWorkspace } = useCurrentWorkspace();
  const { canvas, isLoading: isLoadingCanvas } = useCanvas({
    canvasId,
    workspaceId: currentWorkspace?.id,
  });
  const { mutateAsync: updateCanvas } = usePrivateCanvasesUpdateCanvasMutation();
  const { showToast } = useToasts();

  const {
    formState: { isSubmitting },
    handleSubmit,
    register,
    reset,
  } = useForm<EditCanvasDetailsModalInput>({
    defaultValues: { canvasName: canvas?.title ?? '' },
    resolver: yupResolver(EditCanvasDetailsModalValidationSchema),
  });

  useEffect(() => {
    reset({ canvasName: canvas?.title ?? '' });
  }, [canvas?.title, reset]);

  const onSubmit = useCallback(
    async (values: EditCanvasDetailsModalInput) => {
      if (!currentWorkspace?.id) {
        return;
      }
      try {
        await updateCanvas({
          workspaceId: currentWorkspace.id,
          canvases: [{ canvasId, title: values.canvasName }],
        });
        onClose();
      } catch (error) {
        showToast('There was an error updating the Canvas.');
        reportErrorToBugsnag({
          error,
          context: 'Failed to update canvas',
          metadata: { canvasId },
        });
      }
    },
    [canvasId, currentWorkspace?.id, onClose, showToast, updateCanvas],
  );

  const handleFormSubmit = useMemo(() => handleSubmit(onSubmit), [handleSubmit, onSubmit]);

  return (
    <Modal isOpen onDismiss={onClose} data-testid="EDIT_CANVAS_DETAILS_MODAL">
      <header className="mb-4 flex justify-between gap-4">
        <ModalTitle data-testid="EDIT_CANVAS_DETAILS_MODAL_TITLE">Edit Details</ModalTitle>
        <ModalCloseButton className="shrink-0" onClick={onClose} />
      </header>
      <form id={formId} onSubmit={handleFormSubmit}>
        <div className="mb-6">
          <Label htmlFor={`${formId}-canvasName`}>Canvas name</Label>
          <Input
            id={`${formId}-canvasName`}
            autoComplete="off"
            autoFocus
            className="w-full"
            disabled={isLoadingCanvas}
            size="large"
            {...register('canvasName')}
          />
        </div>
        <footer className="flex justify-end gap-3">
          <Button
            appearance="ghost"
            color="gray"
            data-testid="EDIT_CANVAS_DETAILS_MODAL_CANCEL"
            size="large"
            type="button"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            appearance="filled"
            color="blue"
            data-testid="EDIT_CANVAS_DETAILS_MODAL_SAVE"
            disabled={isLoadingCanvas || isSubmitting}
            isLoading={isSubmitting}
            size="large"
            type="submit"
          >
            Save
          </Button>
        </footer>
      </form>
    </Modal>
  );
});

EditCanvasDetailsModal.displayName = 'EditCanvasDetailsModal';
